import {
  Backdrop,
  Badge,
  Box,
  CircularProgress,
  Divider,
  Grid2,
  Tooltip,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react";
import { useStores } from "../../../../use-stores";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../../../models/Employees/EmployeeRoles";
import {
  isPodOrderType,
  POD_PROJECT_OPS_DISABLED_MESSAGE,
} from "../../../../../../models/Project/OrderType";
import {
  PurchaseRequestProductRequestBody,
  PurchaseRequestSkuVariantRequestBody,
} from "../../../../../../models/PurchaseRequest/PurchaseRequestProductRequestBody";
import {
  isValidPurchaseRequestSkuVariant,
  toPurchaseRequestSkuVariantRequestBody,
} from "../../../../../../utils/purchaseRequestSkuVariants";
import PermissionBlockLabel from "../../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CreatePrIcon from "../../../../../../assets/create-pr-icon.svg";
import CreatePurchaseRequestPopup from "./CreatePrPopup";
import CustomerHeading from "../../../components/projects/projectDetails/CustomerHeading";
import PodProjectOpsDisabledBanner from "../../../components/projects/PodProjectOpsDisabledBanner";
import SourcingOpsTable from "../../../components/projects/sourcing-ops/SourcingOpsTable";
import DownloadIcon from "@mui/icons-material/Download";

const SourcingOps = () => {
  const navigate = useNavigate();
  const {
    projectManagementStore,
    userStore,
    orderStore,
    projectSourcingOpsStore,
  } = useStores();
  const theme = useTheme();
  const { showError, showSuccess } = useNotification();
  const [loading, setLoading] = useState(false);
  const params = useParams();
  const projectId = Number(params["project-id"]);
  const [canOpenCreatePrPopup, setCanOpenCreatePrPopup] = useState(false);

  const isPodOrder = isPodOrderType(orderStore.orderBasicDetails?.type_id);
  const isSourcingMutationLocked =
    isPodOrder ||
    projectManagementStore.currentProjectDetails?.archived === 1;

  const handleOpenCreatePrPopup = () => {
    if (isPodOrder) {
      showError("Error", POD_PROJECT_OPS_DISABLED_MESSAGE);
      return;
    }
    if ((projectSourcingOpsStore.purchaseRequestProducts?.length ?? 0) > 0) {
      const productIds = projectSourcingOpsStore.purchaseRequestProducts
        ?.map((product) => product.id)
        .map(Number);
      projectSourcingOpsStore
        .getPurchaseRequestProductsInsight(projectId, productIds!)
        .catch((error) => {
          showError(
            "Error occurred",
            error?.response?.data?.message ||
            "Failed to fetch purchase request products insights, please try again later",
          );
        });
    }
    if (canOpenCreatePrPopup) {
      setCanOpenCreatePrPopup(false);
    } else {
      setCanOpenCreatePrPopup(true);
    }
  };

  useEffect(() => {
    if (!orderStore.orderBasicDetails) {
      orderStore.getOrderBasicDetails(projectId).catch((err) => {
        setLoading(false);
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage ||
          "Failed to fetch order details, please try again later",
        );
      });
    }

    projectSourcingOpsStore.clearExistingPrProducts();

    getSourcingOpsProducts(projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSourcingOpsProducts = (projectId: number) => {
    projectSourcingOpsStore
      .getProjectSourcingOpsProducts(projectId)
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage ||
          "Failed to fetch order details, please try again later",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleNavigateByProductId = (id: number) => {
    navigate(
      `/projects/sourcing-ops-details?project-id=${projectId}&product-id=${id}`,
    );
  };

  const handleDownloadLink = (url: string) => {
    let a = document.createElement("a");
    a.href = url;
    a.click();
  };

  const handlePr = () => {
    if (loading) {
      return;
    }

    // const validateVariant = (
    //   variant: ProductVariantData,
    //   product: ProjectSourcingProduct
    // ) => {
    //   const hasValidQuantityAndPrice =
    //     variant.quantity &&
    //     variant.costPrice &&
    //     variant.quantity > 0 &&
    //     variant.costPrice > 0;

    //   if (!hasValidQuantityAndPrice) return false;

    //   const availableOptions =
    //     projectSourcingOpsStore.getAvailableOptions(product);
    //   return availableOptions.every(
    //     (option) => variant.selectedOptions[option.optionId]?.trim() !== ""
    //   );
    // };

    // const getVariantIds = (
    //   variant: ProductVariantData,
    //   product: ProjectSourcingProduct
    // ) => {
    //   const variantIds: number[] = [];
    //   Object.entries(variant.selectedOptions).forEach(([, selectedValue]) => {
    //     const matchingVariant = product.product_variants?.find(
    //       (v: ProductVariant) =>
    //         v.title === selectedValue || v.value === selectedValue
    //     );
    //     if (matchingVariant?.id) {
    //       variantIds.push(matchingVariant.id);
    //     }
    //   });
    //   return variantIds;
    // };

    // const generateSku = (
    //   variant: ProductVariantData,
    //   product: ProjectSourcingProduct
    // ) => {
    //   const selectedValues = Object.values(variant.selectedOptions);
    //   return selectedValues.length > 0
    //     ? selectedValues.join("-").toLowerCase()
    //     : product.sku || "";
    // };

    const selectedProductIds =
      projectSourcingOpsStore.purchaseRequestProducts?.map((p) => p.id) || [];

    for (const productId of selectedProductIds) {
      if (!productId) continue;

      const product = projectSourcingOpsStore.products?.find(
        (p) => p.id === productId,
      );
      if (!product) continue;

      // const productVariants =
      //   projectSourcingOpsStore.selectedProductVariants.get(productId);
      // if (!productVariants?.length) {
      //   showError(
      //     "Error occurred",
      //     `Please add at least one variant for product: ${
      //       product.name || productId
      //     }`
      //   );
      //   return;
      // }

      // const allVariantsValid = productVariants.every((variant) => {
      //   const hasValidQuantityAndPrice =
      //     variant.quantity &&
      //     variant.costPrice &&
      //     variant.quantity > 0 &&
      //     variant.costPrice > 0;

      //   if (!hasValidQuantityAndPrice) {
      //     return false;
      //   }

      //   const availableOptions =
      //     projectSourcingOpsStore.getAvailableOptions(product);
      //   const hasAllOptionsSelected = availableOptions.every(
      //     (option) =>
      //       variant.selectedOptions[option.optionId] &&
      //       variant.selectedOptions[option.optionId].trim() !== ""
      //   );

      //   return hasAllOptionsSelected;
      // });

      // if (!allVariantsValid) {
      //   showError(
      //     "Error occurred",
      //     `Please fill complete data (quantity, cost price, and all options) for ALL variants of product: ${
      //       product.name || productId
      //     }`
      //   );
      //   return;
      // }
    }

    const prProducts: PurchaseRequestProductRequestBody[] = [];
    const requestSkuVariants: PurchaseRequestSkuVariantRequestBody[] = [];

    projectSourcingOpsStore.purchaseRequestProducts?.forEach((prProduct) => {
      const product = projectSourcingOpsStore.products?.find(
        (p) => p.id === prProduct.id,
      );
      if (!product) return;

      const productSkuVariants = (
        projectSourcingOpsStore.selectedSkuVariants.get(prProduct.id || 0) || []
      ).filter(isValidPurchaseRequestSkuVariant);

      if (productSkuVariants.length === 0) return;

      const quantity = productSkuVariants.reduce(
        (total, variant) => total + (variant.quantity || 0),
        0,
      );
      const amount = productSkuVariants.reduce(
        (total, variant) =>
          total + (variant.quantity || 0) * (variant.cost_price || 0),
        0,
      );
      const averageCostPrice = quantity > 0 ? amount / quantity : 0;

      prProducts.push({
        id: prProduct.id,
        description: prProduct.description || product.description,
        quantity,
        cost_price: averageCostPrice,
        rate: averageCostPrice,
        sku: product.sku || undefined,
        gst_percentage: product.gst_percentage || prProduct.gst_percentage,
      });

      requestSkuVariants.push(
        ...productSkuVariants.map((variant) =>
          toPurchaseRequestSkuVariantRequestBody(variant, prProduct.id!),
        ),
      );
    });

    // projectSourcingOpsStore.selectedProductVariants.forEach(
    //   (variants, productId) => {
    //     const product = projectSourcingOpsStore.products?.find(
    //       (p) => p.id === productId
    //     );
    //     if (!product) return;

    //     variants.forEach((variant) => {
    //       if (!validateVariant(variant, product)) return;

    //       const variantIds = getVariantIds(variant, product);
    //       if (variantIds.length === 0) return;
    //       if (variant.quantity && variant.costPrice) {
    //         prProducts.push({
    //           id: productId,
    //           description: product.description,
    //           quantity: variant.quantity,
    //           cost_price: variant.costPrice,
    //           rate: variant.costPrice,
    //           variant_ids: variantIds,
    //           sku: generateSku(variant, product),
    //         });
    //       }
    //     });
    //   }
    // );

    if (prProducts.length === 0) {
      showError(
        "Error occurred",
        "Please add at least one product with quantity and cost price",
      );
      return;
    }

    if (!projectSourcingOpsStore.purchaseRequestBody.expected_delivery_date) {
      showError("Error occurred", "Please select expected delivery date");
      return;
    }

    if (
      projectSourcingOpsStore.purchaseRequestBody.other_charges === undefined ||
      projectSourcingOpsStore.purchaseRequestBody.other_charges === null
    ) {
      showError("Error occurred", "Please fill other charges");
      return;
    }

    const vendorId = Array.isArray(
      projectSourcingOpsStore.purchaseRequestBody.vendor_id,
    )
      ? Number(projectSourcingOpsStore.purchaseRequestBody.vendor_id[0].id)
      : Number(projectSourcingOpsStore.purchaseRequestBody.vendor_id);

    if (!vendorId) {
      showError("Error occurred", "Please select vendor");
      return;
    }

    if (!projectSourcingOpsStore.purchaseRequestBody.warehouse_id) {
      showError("Error occurred", "Please select warehouse");
      return;
    }

    setLoading(true);
    projectSourcingOpsStore
      .createPurchaseRequest(
        projectId,
        vendorId,
        prProducts,
        requestSkuVariants,
        projectSourcingOpsStore.purchaseRequestBody
          .expected_delivery_date as Date,
        projectSourcingOpsStore.purchaseRequestBody
          .is_urgent_sourcing_request || false,
        projectSourcingOpsStore.purchaseRequestBody
          .is_urgent_sourcing_request
          ? projectSourcingOpsStore.purchaseRequestBody
              .urgent_payment_percentage ?? null
          : null,
        projectSourcingOpsStore.purchaseRequestBody.warehouse_id,
        projectSourcingOpsStore.purchaseRequestBody.warehouse_address,
        projectSourcingOpsStore.purchaseRequestBody.other_charges,
      )
      .then(() => {
        projectSourcingOpsStore.clearExistingPrProducts();
        projectSourcingOpsStore.updateCity();
        projectSourcingOpsStore.updateWarehouse();
        projectSourcingOpsStore.savePrDetails({ vendor_id: undefined });
        handleOpenCreatePrPopup();
        projectSourcingOpsStore.getProjectSourcingOpsProducts(projectId);
        showSuccess("PR Created Successfully");
      })
      .catch((err) =>
        showError(
          "Error occurred",
          err?.response?.data?.message ||
          "Failed to fetch details, please try again later",
        ),
      )
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      {loading ? (
        <Backdrop
          sx={(theme) => ({
            color: theme.palette.customColors.white,
            zIndex: theme.zIndex.drawer + 1,
          })}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : (
        <>
          {userStore.hasPermission(EmployeeRoles.VIEW_ORDER) ? (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <CustomerHeading
                    brandName={
                      projectManagementStore.currentProjectDetails
                        .customer_name || ""
                    }
                    label={orderStore.orderBasicDetails?.label || ""}
                    moduleName="Sourcing Ops"
                    orderId={projectId}
                    progressValue={
                      projectSourcingOpsStore.sourcingProgress || undefined
                    }
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  {orderStore.orderBasicDetails?.size_mix_url && (
                    <AppButton
                      label="Size Chart"
                      onClick={() =>
                        handleDownloadLink(
                          orderStore.orderBasicDetails?.size_mix_url!,
                        )
                      }
                      fontWeight={600}
                      fontSize="1rem"
                      variant="grayscale"
                      startIcon={<DownloadIcon />}
                    />
                  )}

                  {userStore.hasPermission(
                    [
                      EmployeeRoles.CREATE_PR_AND_PO,
                      EmployeeRoles.VIEW_COST_PRICE,
                      EmployeeRoles.CHANGE_COST_PRICE,
                    ],
                    true,
                  ) && (
                      <Tooltip
                        title={
                          isPodOrder ? POD_PROJECT_OPS_DISABLED_MESSAGE : ""
                        }
                      >
                        <span>
                          <Badge
                            badgeContent={
                              projectSourcingOpsStore.purchaseRequestProducts &&
                                projectSourcingOpsStore.purchaseRequestProducts
                                  .length > 0
                                ? projectSourcingOpsStore.purchaseRequestProducts
                                    .length
                                : "0"
                            }
                            sx={{
                              "& .MuiBadge-badge": {
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.customColors.white,
                                fontSize: "11.43px",
                                fontWeight: 500,
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                              },
                            }}
                          >
                            <AppButton
                              label="Create PR"
                              onClick={handleOpenCreatePrPopup}
                              fontWeight={600}
                              fontSize="1rem"
                              customStyles={{
                                height: "2.563rem",
                                width: "9.438rem",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              variant="secondary"
                              endIcon={CreatePrIcon}
                              disabled={
                                (projectSourcingOpsStore.purchaseRequestProducts
                                  ?.length ?? 0) === 0 ||
                                isSourcingMutationLocked
                              }
                            />
                          </Badge>
                        </span>
                      </Tooltip>
                    )}
                </Box>
              </Box>
              {isPodOrder ? <PodProjectOpsDisabledBanner /> : null}
              <Divider sx={{ mt: 2 }} />
              <SourcingOpsTable
                archived={isSourcingMutationLocked ? 1 : 0}
                maxHeight={window.innerHeight - 350}
                handleNavigateById={(id: number) =>
                  handleNavigateByProductId(id)
                }
              />

              {canOpenCreatePrPopup && !isPodOrder && (
                <CreatePurchaseRequestPopup
                  orderId={projectId}
                  onClose={() => handleOpenCreatePrPopup()}
                  handlePr={handlePr}
                  showSummary={true}
                  showGmailRecipients={false}
                  enableProductSearch={false}
                />
              )}
            </Box>
          ) : (
            <PermissionBlockLabel />
          )}
        </>
      )}
    </Grid2>
  );
};

export default observer(SourcingOps);
